import { useEffect, useRef, useState } from 'react';

import { useRevalidator } from '@remix-run/react';
import pRetry from 'p-retry';

const DEFAULT_MAX_ATTEMPTS = 10;
const DEFAULT_INTERVAL_MS = 3000;

export type AccessPollStatus = 'idle' | 'polling' | 'granted' | 'timeout';

/**
 * Owns the fetch and its error handling: resolve `true` when access is granted,
 * `false` to keep polling; a throw counts as a failed (retryable) attempt.
 */
export type CheckAccess = () => Promise<boolean>;

/**
 * Builds a `CheckAccess` over one of the access resource routes. They always 200 by
 * design (failures are caught server-side), so a non-OK is a transient infra blip
 * (LB/deploy) rather than a reason to give up: throw and let the poll retry.
 */
export const pollAccessRoute =
  (url: string, field: string): CheckAccess =>
  async () => {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}`);
    }

    const data: Record<string, boolean> = await response.json();
    return Boolean(data[field]);
  };

interface UseAccessPollParams {
  enabled: boolean;
  checkAccess: CheckAccess;
  maxAttempts?: number;
  intervalMs?: number;
}

// Mirrors ProductContentWrapper's order-status poll: beforeunload guard + AbortController + p-retry recipe
const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  // Modern browsers ignore custom messages and show their own
  event.returnValue = 'Your order is not ready yet. Please do not close this tab.';
  return '';
};

export const useAccessPoll = ({
  enabled,
  checkAccess,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  intervalMs = DEFAULT_INTERVAL_MS,
}: UseAccessPollParams): AccessPollStatus => {
  const { revalidate } = useRevalidator();
  const [status, setStatus] = useState<AccessPollStatus>('idle');

  // Latest-ref so an unstable checkAccess identity can't restart the poll;
  // the poll's lifecycle is driven by `enabled` alone.
  const checkAccessRef = useRef(checkAccess);
  checkAccessRef.current = checkAccess;

  useEffect(() => {
    if (!enabled || maxAttempts <= 0) return;

    let cancelled = false;
    const abortController = new AbortController();
    setStatus('polling');

    const attempt = async (): Promise<void> => {
      if (!(await checkAccessRef.current())) {
        throw new Error('Access not granted yet');
      }
    };

    const run = async () => {
      try {
        window.addEventListener('beforeunload', handleBeforeUnload);

        await pRetry(attempt, {
          retries: maxAttempts - 1,
          factor: 1,
          minTimeout: intervalMs,
          maxTimeout: intervalMs,
          signal: abortController.signal,
        });

        if (!cancelled) {
          setStatus('granted');
          // Single, deliberate revalidation: the awaiting-access step's data depends on
          // membership/access, which changed server-side while we were polling.
          revalidate();
        }
      } catch {
        if (!cancelled) setStatus('timeout');
      } finally {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    };

    run();

    return () => {
      cancelled = true;
      abortController.abort();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, maxAttempts, intervalMs, revalidate]);

  return status;
};
